import {
  LOADING_LOGO_LETTERS,
  LOADING_LOGO_MARK,
  LOADING_LOGO_VIEW_BOX,
  LoadingLogoGlyph,
} from './app-loading-logo.geometry';

export interface LoadingTimings {
  readonly minimumDisplayMs: number;
  readonly openingFadeMs: number;
  readonly bounceCycleMs: number;
  readonly bounceStartMs: number;
  readonly markSettleMs: number;
  readonly letterUnfoldStartMs: number;
  readonly letterUnfoldDurationMs: number;
  readonly letterStaggerMsPerPx: number;
  readonly letterUnfoldOffsetPx: number;
  readonly markInkFadeMs: number;
  readonly holdMs: number;
  readonly exitFadeMs: number;
  readonly introMs: number;
  readonly introOffsetPx: number;
}

/**
 * The loading screen's timing table (SB-3521). The spec requires these be
 * configuration rather than constants buried in the animation, because the
 * choreography is tuned with design.
 *
 * The dynamic phase lasts as long as the preload does, floored only by the
 * anti-blink minimum below. It is never padded out to fit the choreography in:
 * the spec's US-3 arithmetic and its "elapsed time alone never changes the
 * screen" both require the bounce to be the load's length, not a set piece.
 *
 * The spec's US-2 render-delay guard - no loader at all when the preload
 * completes inside the first 300 ms - is deliberately not implemented. Design
 * confirmed the always-show behaviour is what they want, so the screen renders
 * on every covered load and there is no fast path. US-2 and the "Guards"
 * paragraph on the spec page are stale against that decision and need syncing.
 *
 * This is also why the floor below is what it is: with no fast path, a quick
 * load is the common case rather than the exception, so the floor has to be
 * long enough to show the hop or the gesture is truncated on most loads.
 */
export const LOADING_TIMINGS: LoadingTimings = {
  /**
   * Anti-blink floor, measured from first paint, on every path.
   *
   * Once the screen is up it is up for at least this long, so a preload that
   * lands a few frames later cannot make it flash. It is the only thing gating
   * the reveal - nothing else holds the dynamic phase open.
   * See `AppLoadingService.minimumDynamicMs`.
   */
  minimumDisplayMs: 500,
  /**
   * The screen's own arrival - the whole surface fading up from transparent.
   *
   * The mirror of {@link LoadingTimings.exitFadeMs}: the screen no longer snaps
   * into existence on the first frame any more than it cuts out on the last one.
   * It layers under the logo block's longer entrance rather than replacing it,
   * so the first few frames come in softer while the lift still reads.
   */
  openingFadeMs: 300,
  /** One full bounce of the O during the load-driven phase. */
  bounceCycleMs: 1_500,
  /**
   * When the O's first hop begins, measured from the loader appearing.
   *
   * Deliberately NOT the logo block's entrance. Hanging the hop off `introMs`
   * put its first frame at 800ms, which is at or past the reveal for any load
   * the anti-blink floor governs - so the bounce was cancelled before it ever
   * drew and the O simply never moved. Tying it to the screen's own arrival
   * instead answers the original objection to starting at 0 (the O squashing
   * while the surface was still fading up) without making the hop wait out a
   * lift that runs three times longer than the fade.
   *
   * At the floor this shows the anticipation squash and the start of the rise,
   * and the mark's 180ms ease-out then resolves it into the settle, so the hop
   * reads as landing into the wordmark rather than being cut off. A full hop
   * (motion completes at 55% of the cycle, ~825ms) is only ever seen when the
   * preload actually runs that long - which is the point.
   *
   * This is design-tuned: it is the knob to turn if the gesture reads wrong.
   */
  bounceStartMs: 300,
  /** The O shrinking and gliding into its slot in the wordmark. */
  markSettleMs: 500,
  /** When the first letter starts unfolding, measured from the start of the reveal. */
  letterUnfoldStartMs: 600,
  /** How long a single letter takes to unfold. */
  letterUnfoldDurationMs: 800,
  /** Extra delay per rendered pixel of distance from the O. See {@link letterUnfoldDelayMs}. */
  letterStaggerMsPerPx: 1.05,
  /**
   * How far a letter is pulled in toward the O before it unfolds, in RENDERED
   * pixels. See {@link letterUnfoldOffsetUnits} for why that distinction matters.
   */
  letterUnfoldOffsetPx: 14,
  /** The O resolving from loading indigo to brand ink. */
  markInkFadeMs: 900,
  /**
   * How long the completed logo holds before the exit.
   *
   * 300 is the value in the spec's configurable-timings table. The same page's
   * prose and its Gherkin both say 600, and its "approximately 2.3-2.5 s"
   * static-phase figure only works at 600 - at 300 the static phase plus exit
   * comes to 2.13-2.18 s.
   *
   * Design has confirmed 300, so the table was right and the prose, the Gherkin
   * and the 2.3-2.5 s figure are all stale against it. `app-loading.config.spec`
   * pins the confirmed number; the spec page still needs the other three fixed.
   */
  holdMs: 300,
  /** The fade into the destination page. Never an instant cut. */
  exitFadeMs: 300,
  /** The logo block's entrance when the loader first appears. */
  introMs: 800,
  introOffsetPx: 12,
};

/**
 * The share of the bounce cycle its motion occupies. The keyframes land the
 * last movement at 55% and hold the remaining 45% at rest - design's breath,
 * not dead time, which is why the cycle cannot simply be shortened.
 */
export const BOUNCE_MOTION_FRACTION = 0.55;

/**
 * How long one complete hop takes, measured from the loader appearing.
 *
 * Derived from the choreography rather than picked: the hop's start plus the
 * moving part of the cycle. At the shipped numbers that is 1125ms.
 */
export const oneWholeHopMs = (): number =>
  LOADING_TIMINGS.bounceStartMs +
  LOADING_TIMINGS.bounceCycleMs * BOUNCE_MOTION_FRACTION;

/**
 * Top-level route paths the loading screen does **not** cover.
 *
 * A denylist, and the direction is the point: the screen covers one first
 * navigation wherever it goes, exactly as the loader this replaced did. That
 * loader had no route logic at all - just a `delay(1000)` carrying the comment
 * "avoid animation flickering" - because the first navigation *moves*. `/`
 * redirects to `login`, and `LoginPage` sends a signed-in visitor on to
 * `/chats/teammates`. Answering "is this route covered" per hop makes the
 * answer change underneath a load still in flight, and the screen blips in and
 * out across them.
 *
 * `/admin` is the one exclusion. It declares no preload resolvers of its own -
 * its children load their own slices, and its `AdminTeammatesActions
 * .loadTeammates` is a different action from the one the preload watches - so
 * the screen there was multi-second ceremony over a page it knew nothing about,
 * handing over to a shell still fetching.
 */
export const LOADING_EXCLUDED_ROOT_PATHS: readonly string[] = ['admin'];

/**
 * Whether a first load at this path gets the loading screen.
 *
 * Decided from the URL rather than from auth state because it has to be known
 * synchronously, before anything paints - there is no session check that
 * resolves in time. Being a denylist, an unknown or unmatched URL is covered,
 * which is the harmless direction here: the screen is one continuous cover over
 * whatever the first navigation turns out to be.
 */
export const showsLoadingScreenFor = (pathname: string): boolean => {
  const [rootSegment = ''] = pathname.split('/').filter(Boolean);
  return !LOADING_EXCLUDED_ROOT_PATHS.includes(rootSegment);
};

/**
 * Top-level paths whose subtree declares the three resolvers the preload
 * watches (`chatsResolver`, `assistantsResolver`, `teammatesResolver`).
 *
 * Deliberately **not** the same question as {@link showsLoadingScreenFor}, and
 * keeping them apart is the point. The screen asks "should I cover this load",
 * and the answer is nearly always yes. The retry window asks "has the thing I
 * retry actually run yet", and the answer is only yes on the chats subtree.
 * Answering both with one signal closed the retry window on the `/login` hop,
 * before the preload had been dispatched at all.
 *
 * `app-loading.routes.spec.ts` derives this set from the route table.
 */
export const LOADING_PRELOAD_ROOT_PATHS: readonly string[] = ['chats'];

/**
 * Whether a navigation to this URL is the one that runs the preload.
 *
 * Takes a serialized router URL (a `urlAfterRedirects`, or the in-flight
 * navigation's `finalUrl` or `extractedUrl`), which carries the query string,
 * so the path is split off before matching.
 */
export const carriesPreloadFor = (url: string): boolean => {
  const [path = ''] = url.split(/[?#]/);
  const [rootSegment = ''] = path.split('/').filter(Boolean);
  return LOADING_PRELOAD_ROOT_PATHS.includes(rootSegment);
};

/** Responsive sizing: `clamp(min, ratio x stage width, max)`, in CSS pixels. */
export const LOADING_SIZING = {
  mark: { minPx: 56, maxPx: 80, stageRatio: 0.11 },
  wordmark: { minPx: 20, maxPx: 28, stageRatio: 0.06 },
} as const;

export interface LoadingSizeRule {
  readonly minPx: number;
  readonly maxPx: number;
  readonly stageRatio: number;
}

/** `min(max, max(min, ratio x stageWidth))`, straight from the spec's validation notes. */
export const resolveSize = (
  stageWidthPx: number,
  rule: LoadingSizeRule,
): number =>
  Math.min(rule.maxPx, Math.max(rule.minPx, rule.stageRatio * stageWidthPx));

/** Rendered width of the wordmark, derived from its rendered height. */
export const wordmarkWidthPx = (wordmarkHeightPx: number): number =>
  (wordmarkHeightPx / LOADING_LOGO_VIEW_BOX.height) *
  LOADING_LOGO_VIEW_BOX.width;

/**
 * When a letter starts unfolding, relative to the start of the static reveal.
 *
 * The stagger is measured in **rendered** pixels, not viewBox units. The spec
 * gives both "~1.05 ms/px" and a total static cost of "approximately 2.3-2.5 s",
 * and only the rendered-pixel reading satisfies both: in viewBox units the last
 * letter alone would start at 600 + 1170 ms and push the total past 3.4 s. At
 * the wordmark's 20-28px height range this yields 2.43-2.48 s, which is the
 * stated range, and it explains why the spec quotes a range rather than a
 * single number - the stagger scales with the viewport.
 */
export const letterUnfoldDelayMs = (
  glyph: LoadingLogoGlyph,
  wordmarkWidth: number,
): number => {
  const scale = wordmarkWidth / LOADING_LOGO_VIEW_BOX.width;
  return (
    LOADING_TIMINGS.letterUnfoldStartMs +
    glyph.distanceFromMark * scale * LOADING_TIMINGS.letterStaggerMsPerPx
  );
};

/**
 * How far a letter starts from its final position, in viewBox units, signed so
 * the letters unfold *outward* from the O - those left of it start pushed right,
 * those right of it start pushed left.
 *
 * The configured 14 is RENDERED pixels, but a transform on a path inside the SVG
 * is expressed in user units, and this viewBox is 1440 wide against a wordmark
 * rendered at roughly 222px - so one user unit is about 0.15px. Writing 14
 * straight in moves each letter about 2px, which is close enough to nothing to
 * look like the unfold simply is not working. Exactly the trap the stagger has.
 */
export const letterUnfoldOffsetUnits = (
  glyph: LoadingLogoGlyph,
  wordmarkWidth: number,
): number => {
  const units =
    (LOADING_TIMINGS.letterUnfoldOffsetPx * LOADING_LOGO_VIEW_BOX.width) /
    wordmarkWidth;
  return glyph.centerX < LOADING_LOGO_MARK.centerX ? units : -units;
};

/**
 * When the O starts resolving to brand ink, relative to the start of the reveal.
 *
 * A beat after the first letter begins unfolding, so the shift to ink reads as
 * part of the unfold rather than as an event of its own.
 */
export const MARK_INK_DELAY_MS = LOADING_TIMINGS.letterUnfoldStartMs + 50;

/** When the last letter finishes moving - the anchor the hold and exit hang off. */
export const lastLetterLandingMs = (wordmarkWidth: number): number =>
  Math.max(
    ...LOADING_LOGO_LETTERS.map((glyph) =>
      letterUnfoldDelayMs(glyph, wordmarkWidth),
    ),
  ) + LOADING_TIMINGS.letterUnfoldDurationMs;

/**
 * How long the static reveal runs before the exit fade starts.
 *
 * Computed from the last letter's landing plus the hold, never from an absolute
 * timestamp - that is what keeps the choreography intact when the stagger
 * changes with viewport width.
 *
 * Under reduced motion there is no unfold to wait for: the finished logo is
 * shown at once and only the hold applies.
 */
export const staticPhaseDurationMs = (
  wordmarkWidth: number,
  prefersReducedMotion = false,
): number =>
  prefersReducedMotion
    ? LOADING_TIMINGS.holdMs
    : lastLetterLandingMs(wordmarkWidth) + LOADING_TIMINGS.holdMs;
